import type {
  ManualEntryCategory,
  ManualEntryStructuredData,
  ManualEntryFeePrize,
  ManualEntryDeadline,
} from './manualEntryTypes';

export const createEmptyStructuredData = (): ManualEntryStructuredData => ({
  categories: [],
  deadlines: [],
  fees_prizes: [],
  requirements: {
    eligibility: [],
    restrictions: [],
    submission_types: [],
  },
  contacts: [],
  judging: {
    criteria: [],
    process: '',
  },
});

export const normaliseStructuredData = (
  data: ManualEntryStructuredData | undefined
): ManualEntryStructuredData => {
  if (!data) return createEmptyStructuredData();
  return {
    categories: data.categories ?? [],
    deadlines: data.deadlines ?? [],
    fees_prizes: data.fees_prizes ?? [],
    requirements: {
      eligibility: data.requirements?.eligibility ?? [],
      restrictions: data.requirements?.restrictions ?? [],
      submission_types: data.requirements?.submission_types ?? [],
    },
    contacts: data.contacts ?? [],
    judging: {
      criteria: data.judging?.criteria ?? [],
      process: data.judging?.process ?? '',
    },
  };
};

export const hasStructuredData = (data: ManualEntryStructuredData | undefined): boolean => {
  if (!data) return false;
  const { categories, deadlines, fees_prizes, requirements, contacts, judging } = data;

  return Boolean(
    (categories && categories.length > 0) ||
      (deadlines && deadlines.length > 0) ||
      (fees_prizes && fees_prizes.length > 0) ||
      (requirements && (
        requirements.eligibility.length > 0 ||
        requirements.restrictions.length > 0 ||
        requirements.submission_types.length > 0
      )) ||
      (contacts && contacts.length > 0) ||
      (judging && (judging.criteria.length > 0 || judging.process?.trim()))
  );
};

const header = (title: string) => `### ${title}\n`;

const formatCategories = (categories: ManualEntryCategory[]): string => {
  if (!categories.length) return '';
  const lines = categories.map((category) => {
    const parts = [category.display_name || category.name || 'Unnamed Category'];
    if (category.bisac_code) parts.push(`BISAC: ${category.bisac_code}`);
    if (typeof category.order === 'number') parts.push(`Order: ${category.order}`);
    return `- ${parts.join(' • ')}`;
  });
  return `${header('Categories')}${lines.join('\n')}\n\n`;
};

const formatDeadlines = (deadlines: ManualEntryDeadline[]): string => {
  if (!deadlines.length) return '';
  const lines = deadlines.map((deadline) => {
    const typeLabel = deadline.type ? deadline.type.toUpperCase() : 'Deadline';
    const parts = [`${typeLabel}: ${deadline.date}`];
    if (deadline.description) parts.push(deadline.description);
    return `- ${parts.join(' — ')}`;
  });
  return `${header('Deadlines')}${lines.join('\n')}\n\n`;
};

const formatFees = (fees: ManualEntryFeePrize[]): string => {
  if (!fees.length) return '';
  const lines = fees.map((fee) => {
    const pieces: string[] = [];
    if (typeof fee.fee_amount === 'number') pieces.push(`Fee: ${fee.fee_amount}`);
    if (typeof fee.prize_amount === 'number') pieces.push(`Prize: ${fee.prize_amount}`);
    if (fee.currency) pieces.push(`Currency: ${fee.currency.toUpperCase()}`);
    if (fee.tier) pieces.push(`Tier: ${fee.tier}`);
    if (fee.notes) pieces.push(fee.notes);
    return `- ${pieces.join(' • ')}`;
  });
  return `${header('Fees & Prizes')}${lines.join('\n')}\n\n`;
};

const formatRequirements = (data: ManualEntryStructuredData['requirements']): string => {
  const sections: string[] = [];
  const addSection = (title: string, items: string[]) => {
    if (!items.length) return;
    sections.push(`${title}:`);
    sections.push(...items.map((item) => `- ${item}`));
    sections.push('');
  };

  addSection('Eligibility', data.eligibility);
  addSection('Restrictions', data.restrictions);
  addSection('Submission Types', data.submission_types);

  if (!sections.length) return '';
  return `${header('Requirements')}${sections.join('\n')}\n`;
};

const formatContacts = (contacts: ManualEntryStructuredData['contacts']): string => {
  if (!contacts.length) return '';
  const lines = contacts.map((contact) => {
    const pieces = [contact.name || 'Contact'];
    if (contact.role) pieces.push(contact.role);
    if (contact.department) pieces.push(contact.department);
    if (contact.email) pieces.push(contact.email);
    return `- ${pieces.join(' • ')}`;
  });
  return `${header('Contacts')}${lines.join('\n')}\n\n`;
};

const formatJudging = (judging: ManualEntryStructuredData['judging']): string => {
  const sections: string[] = [];
  if (judging.process?.trim()) {
    sections.push('Process:');
    sections.push(judging.process.trim());
  }
  if (judging.criteria.length) {
    sections.push('Criteria:');
    sections.push(...judging.criteria.map((criterion) => `- ${criterion}`));
  }
  if (!sections.length) return '';
  return `${header('Judging')}${sections.join('\n')}\n`;
};

export const buildStructuredContent = (
  programName: string | undefined,
  data: ManualEntryStructuredData
): string => {
  const title = programName ? `## ${programName} — Structured Manual Entry\n\n` : '## Manual Entry — Structured Data\n\n';
  return (
    title +
    formatCategories(data.categories) +
    formatDeadlines(data.deadlines) +
    formatFees(data.fees_prizes) +
    formatRequirements(data.requirements) +
    formatContacts(data.contacts) +
    formatJudging(data.judging)
  ).trim();
};

const coalesceNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined) return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const normaliseString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export interface CategoryBatchParseResult {
  categories: ManualEntryCategory[];
  errors: string[];
  format: 'csv' | 'json';
}

const requiredFields = ['name', 'display_name'];

const fromJsonValue = (value: unknown): ManualEntryCategory | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  const name = normaliseString(candidate.name);
  const display = normaliseString(candidate.display_name || candidate.displayName);
  if (!name || !display) return null;
  return {
    name,
    display_name: display,
    bisac_code: normaliseString(candidate.bisac_code || candidate.bisac),
    order: coalesceNumber(candidate.order),
  };
};

const parseCsv = (raw: string): CategoryBatchParseResult => {
  const errors: string[] = [];
  const [headerLine, ...rows] = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!headerLine) {
    return { categories: [], errors: ['CSV input is empty.'], format: 'csv' };
  }

  const headers = headerLine.split(',').map((h) => h.trim().toLowerCase());
  const missing = requiredFields.filter((field) => !headers.includes(field));
  if (missing.length) {
    errors.push(`Missing required CSV columns: ${missing.join(', ')}`);
  }

  const categories: ManualEntryCategory[] = [];
  rows.forEach((row, index) => {
    if (!row) return;
    const columns = row.split(',');
    const record: Record<string, string> = {};
    headers.forEach((header, idx) => {
      record[header] = columns[idx] ? columns[idx].trim() : '';
    });

    const name = normaliseString(record.name);
    const display = normaliseString(record.display_name || record.displayname);
    if (!name || !display) {
      errors.push(`Row ${index + 1} missing name or display_name.`);
      return;
    }

    categories.push({
      name,
      display_name: display,
      bisac_code: normaliseString(record.bisac_code || record.bisac),
      order: coalesceNumber(record.order),
    });
  });

  return { categories, errors, format: 'csv' };
};

const parseJson = (raw: string): CategoryBatchParseResult => {
  const errors: string[] = [];
  try {
    const data = JSON.parse(raw);
    const candidates = Array.isArray(data) ? data : data?.categories;
    if (!Array.isArray(candidates)) {
      return {
        categories: [],
        errors: ['JSON must be an array of category objects or { "categories": [...] }'],
        format: 'json',
      };
    }

    const categories: ManualEntryCategory[] = [];
    candidates.forEach((candidate, index) => {
      const normalised = fromJsonValue(candidate);
      if (!normalised) {
        errors.push(`Category at index ${index} is missing required fields.`);
      } else {
        categories.push(normalised);
      }
    });

    return { categories, errors, format: 'json' };
  } catch (error) {
    return {
      categories: [],
      errors: [`JSON parse error: ${(error as Error).message}`],
      format: 'json',
    };
  }
};

export const parseCategoryBatchInput = (raw: string): CategoryBatchParseResult => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { categories: [], errors: ['No data provided.'], format: 'csv' };
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return parseJson(trimmed);
  }

  return parseCsv(trimmed);
};

export const mergeCategories = (
  existing: ManualEntryCategory[],
  incoming: ManualEntryCategory[]
): ManualEntryCategory[] => {
  const map = new Map<string, ManualEntryCategory>();
  existing.forEach((category) => {
    map.set(category.name.toLowerCase(), { ...category });
  });

  incoming.forEach((category) => {
    const key = category.name.toLowerCase();
    const existingCategory = map.get(key);
    if (existingCategory) {
      map.set(key, {
        ...existingCategory,
        display_name: category.display_name || existingCategory.display_name,
        bisac_code: category.bisac_code || existingCategory.bisac_code,
        order: category.order ?? existingCategory.order,
      });
    } else {
      map.set(key, category);
    }
  });

  return Array.from(map.values()).sort((a, b) => {
    if (typeof a.order === 'number' && typeof b.order === 'number') {
      return a.order - b.order;
    }
    if (typeof a.order === 'number') return -1;
    if (typeof b.order === 'number') return 1;
    return a.display_name.localeCompare(b.display_name);
  });
};
